import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import { getCityFromZip } from '../../constants/zipData';

// ─── Unified My Calendar Page ─────────────────────────────────────────────────
function MyCalendarPage({ authUser, vendorProfile, userEvents, setTab }) {
  const [hostEvents, setHostEvents] = useState(userEvents || []);
  const [vendorBookings, setVendorBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [blockedDates, setBlockedDates] = useState(() => { try { return JSON.parse(localStorage.getItem('sjvm_blocked_dates')||'[]'); } catch { return []; } });
  const toggleBlockDate = (ds) => { setBlockedDates(bd => { const next = bd.includes(ds) ? bd.filter(d=>d!==ds) : [...bd,ds]; localStorage.setItem('sjvm_blocked_dates',JSON.stringify(next)); return next; }); };

  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth+1, 0).getDate();
  const dateStr = (d) => `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  const todayStr = today.toISOString().split('T')[0];
  const prev = () => { if(viewMonth===0){setViewMonth(11);setViewYear(y=>y-1);}else setViewMonth(m=>m-1); };
  const next = () => { if(viewMonth===11){setViewMonth(0);setViewYear(y=>y+1);}else setViewMonth(m=>m+1); };
  const fmt12 = (t) => { if(!t)return''; const[h,m]=t.split(':').map(Number); return `${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`; };
  const fmtD = (d) => d ? new Date(d+'T12:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—';

  useEffect(() => {
    if (!authUser) { setLoading(false); return; }
    let mounted = true;
    (async () => {
      // Fetch host events
      const { data: evts } = await supabase.from('events').select('*')
        .or(`user_id.eq.${authUser.id},contact_email.ilike.${authUser.email}`)
        .order('date', { ascending: true });
      if (mounted && evts) setHostEvents(evts);

      // Fetch vendor bookings
      let allReqs = [];
      if (vendorProfile?.id) {
        const { data } = await supabase.from('booking_requests').select('*').eq('vendor_id', vendorProfile.id);
        if (data) allReqs = data;
      }
      if (allReqs.length === 0 && vendorProfile?.contact_email) {
        const { data } = await supabase.from('booking_requests').select('*').ilike('host_email', vendorProfile.contact_email);
        if (data) allReqs = data;
      }
      if (allReqs.length === 0 && vendorProfile?.name) {
        const { data } = await supabase.from('booking_requests').select('*').ilike('vendor_name', vendorProfile.name);
        if (data) allReqs = data;
      }
      const seen = new Set(); allReqs = allReqs.filter(r => { if(seen.has(r.id)) return false; seen.add(r.id); return true; });
      if (mounted) setVendorBookings(allReqs);
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, [authUser, vendorProfile]);

  // Navigate to first upcoming event
  useEffect(() => {
    const allDates = [...hostEvents.map(e=>e.date), ...vendorBookings.map(b=>b.event_date)].filter(Boolean).sort();
    const upcoming = allDates.find(d => d >= todayStr) || allDates[0];
    if (upcoming) { const [y,mo] = upcoming.split('-').map(Number); setViewYear(y); setViewMonth(mo-1); }
  }, [loading]);

  if (!authUser) {
    return (
      <div style={{textAlign:'center',padding:'80px 40px',color:'#7a6a5a'}}>
        <div style={{fontSize:48,marginBottom:16}}>📅</div>
        <div style={{fontFamily:'Playfair Display,serif',fontSize:28,marginBottom:12,color:'#1a1410'}}>My Calendar</div>
        <p style={{fontSize:16,maxWidth:440,margin:'0 auto'}}>Log in to see your calendar.</p>
      </div>
    );
  }
  if (loading) return <div style={{textAlign:'center',padding:'80px 20px',color:'#a89a8a',fontSize:16}}>Loading calendar...</div>;

  // Build unified date map
  const dateMap = {};
  hostEvents.forEach(e => {
    if (!e.date) return;
    if (!dateMap[e.date]) dateMap[e.date] = { hostEvents:[], accepted:[], pending:[], service:[] };
    dateMap[e.date].hostEvents.push(e);
  });
  vendorBookings.forEach(b => {
    if (!b.event_date) return;
    if (!dateMap[b.event_date]) dateMap[b.event_date] = { hostEvents:[], accepted:[], pending:[], service:[] };
    const m = vendorProfile?.metadata || {};
    const isService = m.vendorType === 'service' || m.vendorType === 'both' || m.isServiceProvider;
    if (b.status === 'accepted') dateMap[b.event_date].accepted.push(b);
    else if (b.status === 'pending' && isService) dateMap[b.event_date].service.push(b);
    else if (b.status === 'pending') dateMap[b.event_date].pending.push(b);
  });

  const getCellColor = (ds) => {
    const d = dateMap[ds];
    if (!d) return null;
    if (d.accepted.length > 0) return '#1a6b3a'; // green — accepted vendor
    if (d.hostEvents.length > 0) return '#c8a84b'; // gold — host event
    if (d.service.length > 0) return '#2563eb'; // blue — service provider pending
    if (d.pending.length > 0) return '#d4a017'; // yellow — pending vendor
    return null;
  };

  const selectedInfo = selectedDate ? dateMap[selectedDate] : null;

  const acceptedTotal = vendorBookings.filter(b=>b.status==='accepted').length;
  const pendingTotal = vendorBookings.filter(b=>b.status==='pending').length;

  return (
    <div className="section" style={{maxWidth:900}}>
      <div className="section-title">My Calendar</div>
      <p className="section-sub">All your events and bookings in one view</p>

      {/* Legend */}
      <div style={{display:'flex',gap:16,flexWrap:'wrap',marginBottom:20,padding:'10px 16px',background:'#f5f0ea',borderRadius:8,border:'1px solid #e8ddd0'}}>
        {hostEvents.length > 0 && <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'#1a1410'}}><div style={{width:12,height:12,borderRadius:3,background:'#c8a84b'}}/> My Events (Host)</div>}
        {acceptedTotal > 0 && <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'#1a1410'}}><div style={{width:12,height:12,borderRadius:3,background:'#1a6b3a'}}/> Confirmed Bookings</div>}
        {pendingTotal > 0 && <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'#1a1410'}}><div style={{width:12,height:12,borderRadius:3,background:'#d4a017'}}/> Pending Applications</div>}
        {vendorBookings.some(b=>b.status==='pending'&&(vendorProfile?.metadata?.vendorType==='service'||vendorProfile?.metadata?.isServiceProvider)) && <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'#1a1410'}}><div style={{width:12,height:12,borderRadius:3,background:'#2563eb'}}/> Service Provider Apps</div>}
      </div>

      {/* Stats bar */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:16}}>
        {hostEvents.length > 0 && <div style={{background:'#fdf4dc',color:'#7a5a10',border:'1px solid #ffd966',borderRadius:20,padding:'4px 14px',fontSize:12,fontWeight:700}}>{hostEvents.length} event{hostEvents.length!==1?'s':''} posted</div>}
        {acceptedTotal > 0 && <div style={{background:'#d4f4e0',color:'#1a6b3a',border:'1px solid #b8e8c8',borderRadius:20,padding:'4px 14px',fontSize:12,fontWeight:700}}>{acceptedTotal} confirmed booking{acceptedTotal!==1?'s':''}</div>}
        {pendingTotal > 0 && <div style={{background:'#fdf4dc',color:'#7a5a10',border:'1px solid #ffd966',borderRadius:20,padding:'4px 14px',fontSize:12,fontWeight:700}}>{pendingTotal} pending</div>}
      </div>

      {/* Calendar grid */}
      <div style={{background:'#fff',borderRadius:12,border:'1px solid #e8ddd0',overflow:'hidden',marginBottom:20}}>
        <div style={{background:'#1a1410',padding:'16px 20px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <button onClick={prev} style={{background:'none',border:'none',color:'#c8a84b',cursor:'pointer',fontSize:20,fontWeight:700}}>‹</button>
          <div style={{fontFamily:'Playfair Display,serif',fontSize:20,color:'#e8c97a'}}>{MONTHS[viewMonth]} {viewYear}</div>
          <button onClick={next} style={{background:'none',border:'none',color:'#c8a84b',cursor:'pointer',fontSize:20,fontWeight:700}}>›</button>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',textAlign:'center'}}>
          {DAYS.map(d=><div key={d} style={{fontSize:11,color:'#a89a8a',fontWeight:600,padding:'8px 0',borderBottom:'1px solid #e8ddd0'}}>{d}</div>)}
          {Array(firstDay).fill(null).map((_,i)=><div key={'e'+i} style={{minHeight:70,borderRight:'1px solid #f0e8e0',borderBottom:'1px solid #f0e8e0',background:'#fafaf8'}}/>)}
          {Array(daysInMonth).fill(null).map((_,i)=>{
            const d=i+1, ds=dateStr(d);
            const cellColor=getCellColor(ds);
            const isToday=ds===todayStr;
            const isSel=ds===selectedDate;
            const info=dateMap[ds];
            return (
              <div key={d} onClick={()=>setSelectedDate(isSel?null:ds)}
                style={{minHeight:70,borderRight:'1px solid #f0e8e0',borderBottom:'1px solid #f0e8e0',padding:'6px 4px',
                  cursor:'pointer',background:blockedDates.includes(ds)?'#fdecea':isSel?'#fdf9f5':'#fff',
                  boxShadow:isToday?'inset 0 0 0 2px #c8a84b':'none'}}>
                <div style={{fontSize:13,fontWeight:isToday?800:500,color:blockedDates.includes(ds)?'#8b1a1a':isToday?'#c8a84b':'#4a3a28',marginBottom:2}}>{d}{blockedDates.includes(ds)?'⛔':''}</div>
                {info && (
                  <div style={{display:'flex',flexDirection:'column',gap:2}}>
                    {info.hostEvents.map(e=>(
                      <div key={e.id} style={{background:'#c8a84b',color:'#1a1410',borderRadius:3,padding:'1px 4px',fontSize:9,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {e.event_name||e.event_type}
                      </div>
                    ))}
                    {info.accepted.map(b=>(
                      <div key={b.id} style={{background:'#1a6b3a',color:'#fff',borderRadius:3,padding:'1px 4px',fontSize:9,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {b.event_name||'Booking'}
                      </div>
                    ))}
                    {info.pending.map(b=>(
                      <div key={b.id} style={{background:'#d4a017',color:'#fff',borderRadius:3,padding:'1px 4px',fontSize:9,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {b.event_name||'Pending'}
                      </div>
                    ))}
                    {info.service.map(b=>(
                      <div key={b.id} style={{background:'#2563eb',color:'#fff',borderRadius:3,padding:'1px 4px',fontSize:9,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {b.event_name||'Service'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail panel */}
      {selectedDate && (
        <div style={{background:'#fff',borderRadius:12,border:'2px solid #c8a84b',padding:20,marginBottom:20}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <div style={{fontFamily:'Playfair Display,serif',fontSize:20,color:'#1a1410'}}>{fmtD(selectedDate)}</div>
            <div style={{display:'flex',gap:6}}>
              {vendorProfile && (
                <button onClick={()=>toggleBlockDate(selectedDate)} style={{background:blockedDates.includes(selectedDate)?'#d4f4e0':'#fdecea',color:blockedDates.includes(selectedDate)?'#1a6b3a':'#8b1a1a',border:'none',borderRadius:6,padding:'4px 12px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>{blockedDates.includes(selectedDate)?'✓ Unblock Date':'⛔ Block Date'}</button>
              )}
              <button onClick={()=>setSelectedDate(null)} style={{background:'#f5f0ea',border:'1px solid #e0d5c5',borderRadius:6,padding:'4px 12px',fontSize:12,fontWeight:600,cursor:'pointer',color:'#7a6a5a',fontFamily:'DM Sans,sans-serif'}}>Close</button>
            </div>
          </div>
          {blockedDates.includes(selectedDate) && <div style={{background:'#fdecea',borderRadius:6,padding:'8px 12px',marginBottom:12,fontSize:12,color:'#8b1a1a',fontWeight:600}}>This date is marked as unavailable</div>}
          {selectedInfo && (<>
          {/* Host events on this date */}
          {selectedInfo.hostEvents.map(e=>(
            <div key={e.id} style={{background:'#1a1410',borderRadius:10,padding:'14px 18px',marginBottom:10}}>
              <div style={{fontSize:10,letterSpacing:2,textTransform:'uppercase',color:'#c8a84b',marginBottom:4}}>Your Event (Host)</div>
              <div style={{fontFamily:'Playfair Display,serif',fontSize:18,color:'#e8c97a',marginBottom:6}}>{e.event_name||e.event_type}</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:12,fontSize:12,color:'#a89a8a'}}>
                {e.event_type && <span>🎪 {e.event_type}</span>}
                {e.start_time && <span>🕐 {fmt12(e.start_time)}{e.end_time?` – ${fmt12(e.end_time)}`:''}</span>}
                <span>📍 {getCityFromZip(e.zip) || e.zip}</span>
                {e.spots && <span>🏪 {e.spots} spots</span>}
                <span style={{color:e.status==='approved'?'#a3e8bb':'#ffd966'}}>{e.status==='approved'?'Live':e.status}</span>
              </div>
            </div>
          ))}
          {/* Accepted vendor bookings on this date */}
          {selectedInfo.accepted.map(b=>(
            <div key={b.id} style={{background:'#d4f4e0',border:'1px solid #b8e8c8',borderRadius:10,padding:'12px 16px',marginBottom:8}}>
              <div style={{fontSize:10,letterSpacing:2,textTransform:'uppercase',color:'#1a6b3a',marginBottom:4}}>Confirmed Booking (Vendor)</div>
              <div style={{fontWeight:700,fontSize:15,color:'#1a1410'}}>{b.event_name||'Event'}</div>
              <div style={{fontSize:12,color:'#2d7a50',marginTop:4}}>
                {b.event_type&&`${b.event_type} · `}{b.start_time&&`${fmt12(b.start_time)} · `}{getCityFromZip(b.event_zip) || b.event_zip || '—'}
              </div>
              <div style={{fontSize:12,color:'#1a6b3a',marginTop:4}}>Host: {b.host_name||'—'}{b.host_email?` · ${b.host_email}`:''}</div>
            </div>
          ))}
          {/* Pending vendor applications */}
          {selectedInfo.pending.map(b=>(
            <div key={b.id} style={{background:'#fdf4dc',border:'1px solid #ffd966',borderRadius:10,padding:'12px 16px',marginBottom:8}}>
              <div style={{fontSize:10,letterSpacing:2,textTransform:'uppercase',color:'#7a5a10',marginBottom:4}}>Pending Application (Vendor)</div>
              <div style={{fontWeight:700,fontSize:15,color:'#1a1410'}}>{b.event_name||'Event'}</div>
              <div style={{fontSize:12,color:'#7a5a10',marginTop:4}}>
                {b.event_type&&`${b.event_type} · `}Zip {b.event_zip||'—'} · Awaiting host response
              </div>
            </div>
          ))}
          {/* Service provider applications */}
          {selectedInfo.service.map(b=>(
            <div key={b.id} style={{background:'#dbeafe',border:'1px solid #93c5fd',borderRadius:10,padding:'12px 16px',marginBottom:8}}>
              <div style={{fontSize:10,letterSpacing:2,textTransform:'uppercase',color:'#1d4ed8',marginBottom:4}}>Service Provider Application</div>
              <div style={{fontWeight:700,fontSize:15,color:'#1a1410'}}>{b.event_name||'Event'}</div>
              <div style={{fontSize:12,color:'#2563eb',marginTop:4}}>
                {b.event_type&&`${b.event_type} · `}Zip {b.event_zip||'—'} · Awaiting response
              </div>
            </div>
          ))}
      </>)}
        </div>
      )}

      {/* Empty state */}
      {hostEvents.length === 0 && vendorBookings.length === 0 && (
        <div style={{background:'#f5f0ea',border:'1px solid #e8ddd0',borderRadius:12,padding:32,textAlign:'center'}}>
          <div style={{fontSize:32,marginBottom:12}}>📅</div>
          <div style={{fontFamily:'Playfair Display,serif',fontSize:18,color:'#1a1410',marginBottom:8}}>Your calendar is empty</div>
          <p style={{fontSize:13,color:'#7a6a5a',marginBottom:16}}>Add an event as a host or apply to events as a vendor to see them here.</p>
          <div style={{display:'flex',gap:8,justifyContent:'center'}}>
            <button onClick={()=>setTab('host')} style={{background:'#c8a84b',color:'#1a1410',border:'none',borderRadius:8,padding:'10px 20px',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Add an Event</button>
            <button onClick={()=>setTab('opportunities')} style={{background:'#1a1410',color:'#e8c97a',border:'none',borderRadius:8,padding:'10px 20px',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Browse Events</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyCalendarPage;
